import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, Shield, Star, AlertCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const HRAQuestionnaireStreamlined = ({ user, onComplete, onCancel, setNumber }) => {
  const [questionnaire, setQuestionnaire] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const { apiCall } = useApp();

  useEffect(() => {
    loadQuestionnaire();
  }, [user, setNumber]);


  const loadQuestionnaire = async () => {
    try {
      setIsLoading(true);
      // If setNumber is provided, use it; otherwise API will use default set
      const url = setNumber 
        ? `/hra/questionnaire/questions?set_number=${setNumber}`
        : '/hra/questionnaire/questions';
      const response = await apiCall(url, 'GET');
      console.log('Loaded questionnaire:', response);
      setQuestionnaire(response);
      
      // Reset to first question when loading new questionnaire
      setCurrentQuestionIndex(0);
      setResponses({});
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
      setQuestionnaire(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Visibility helpers
  const normalizeYesNo = (v) => (v === true ? 'yes' : v === false ? 'no' : v);
  const isQuestionVisible = (question, resp) => {
    if (!question.visible_if) return true;
    try {
      return Object.entries(question.visible_if).every(([depId, allowedValues]) => {
        const actual = normalizeYesNo(resp[depId]);
        return allowedValues.includes(actual);
      });
    } catch (_) {
      return true;
    }
  };
  const getVisibleQuestions = (resp) =>
    (questionnaire?.questions || []).filter((q) => isQuestionVisible(q, resp));

  const handleAnswer = async (questionId, answer) => {
    // Compute next responses immediately for correct visibility and advancing
    const nextResponses = { ...responses, [questionId]: answer };
    setResponses(nextResponses);

    // Auto-advance to next visible question after a short delay
    setTimeout(() => {
      const visible = getVisibleQuestions(nextResponses);
      // Find current question index within the visible list using id
      const currentId = questionId;
      const visibleIndex = visible.findIndex((q) => q.id === currentId);
      if (visibleIndex > -1 && visibleIndex < visible.length - 1) {
        setCurrentQuestionIndex(visibleIndex + 1);
      } else {
        // All visible questions answered, submit
        handleSubmit(nextResponses);
      }
    }, 500);
  };

  const handleSkipOptional = (questionId) => {
    // For optional questions, set empty response and advance
    const currentQuestion = questionnaire.questions[currentQuestionIndex];
    if (!currentQuestion.required) {
      if (currentQuestion.type === 'multiple_choice') {
        handleAnswer(questionId, []);
      } else {
        handleAnswer(questionId, null);
      }
    }
  };

  const handleSubmit = async (finalResponses) => {
    try {
      setIsSubmitting(true);
      setShowCompletion(true);
      
      // Transform responses to match backend expectations
      const transformedResponses = {
        streamlined: finalResponses,
        set_number: questionnaire.set_number || setNumber  // Use set_number from questionnaire or fallback
      };
      
      const submissionData = {
        questionnaire_version: questionnaire.version,
        responses: transformedResponses
      };
      
      console.log('Submitting HRA data:', submissionData);
      const result = await apiCall('/hra/questionnaire', 'POST', submissionData);
      
      // Show completion animation then redirect
      setTimeout(() => {
        onComplete(result);
      }, 2000);
    } catch (error) {
      console.error('Failed to submit questionnaire:', error);
      setIsSubmitting(false);
      setShowCompletion(false);
    }
  };

  const calculateProgress = () => {
    if (!questionnaire) return 0;
    const visible = getVisibleQuestions(responses);
    return visible.length > 0 ? ((currentQuestionIndex + 1) / visible.length) * 100 : 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your personalized health assessment...</p>
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600">Failed to load questionnaire. Please try again.</p>
        <button
          onClick={loadQuestionnaire}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="text-center py-12">
        <div className="animate-bounce mb-6">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Assessment Complete!</h3>
        <p className="text-gray-600 mb-6">
          Analyzing your responses and calculating your health risk profile...
        </p>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
      </div>
    );
  }

  const visibleQuestions = getVisibleQuestions(responses);
  
  // Check if there are no questions
  if (!visibleQuestions || visibleQuestions.length === 0) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <p className="text-gray-600">No questions available for this assessment.</p>
        <p className="text-sm text-gray-500 mt-2">Please contact support if this issue persists.</p>
        <button
          onClick={loadQuestionnaire}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const currentQuestion = visibleQuestions[currentQuestionIndex];
  
  if (!currentQuestion) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <p className="text-gray-600">Question not found. Please refresh the page.</p>
        <button
          onClick={loadQuestionnaire}
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Refresh
        </button>
      </div>
    );
  }
  
  const progress = calculateProgress();

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {questionnaire.set_name || 'Health Risk Assessment'}
            </h2>
            {questionnaire.set_description && (
              <p className="text-sm text-gray-600 mt-1">{questionnaire.set_description}</p>
            )}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-1" />
            ~{questionnaire.estimated_time_minutes || 10} min
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div 
            className="bg-primary-600 h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <div className="flex justify-between text-sm text-gray-600">
          <span>Question {currentQuestionIndex + 1} of {visibleQuestions.length}</span>
          <span>{Math.round(progress)}% Complete</span>
        </div>
      </div>

      {/* Current Question */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">
          {currentQuestion.question}
        </h3>
        
        <QuestionRenderer 
          question={currentQuestion}
          value={responses[currentQuestion.id]}
          onChange={(answer) => handleAnswer(currentQuestion.id, answer)}
        />
      </div>

      {/* Privacy Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Shield className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Your Privacy Matters</p>
            <p>All responses are encrypted and secure. This assessment provides general health insights and does not replace professional medical advice.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Question Renderer Component for different question types
const QuestionRenderer = ({ question, value, onChange }) => {
  // Local state for dropdown to prevent auto-advance
  const [dropdownLocalValue, setDropdownLocalValue] = useState(value || question.default || '');

  // Update local value when external value changes (e.g., when question changes)
  useEffect(() => {
    setDropdownLocalValue(value || question.default || '');
  }, [question.id, value, question.default]);

  const renderInput = () => {
    switch (question.type) {
      case 'yes_no':
        return (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => onChange(true)}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                value === true
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 hover:border-green-300 hover:bg-green-50'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl mb-2">✓</div>
                <div className="font-medium">Yes</div>
              </div>
            </button>
            <button
              onClick={() => onChange(false)}
              className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                value === false
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-300 hover:border-red-300 hover:bg-red-50'
              }`}
            >
              <div className="text-center">
                <div className="text-2xl mb-2">✗</div>
                <div className="font-medium">No</div>
              </div>
            </button>
          </div>
        );

      case 'single_choice':
        return (
          <div className="space-y-3">
            {question.options.map((option, index) => {
              const optionValue = option.value || option;
              const optionLabel = option.label || option;
              return (
                <button
                  key={index}
                  onClick={() => onChange(optionValue)}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                    value === optionValue
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 hover:border-primary-300 hover:bg-primary-50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full border-2 mr-3 ${
                      value === optionValue ? 'bg-primary-500 border-primary-500' : 'border-gray-400'
                    }`}>
                      {value === optionValue && (
                        <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                      )}
                    </div>
                    <span className="font-medium">{optionLabel}</span>
                  </div>
                </button>
              );
            })}
          </div>
        );

      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {question.options.map((option, index) => {
              const optionValue = option.value || option;
              const optionLabel = option.label || option;
              const isSelected = (value || []).includes(optionValue) || (value || []).includes(option);
              return (
                <button
                  key={index}
                  onClick={() => {
                    const currentValues = value || [];
                    let newValues;
                    
                    if (optionValue === "none" || optionLabel === "None" || option === "None") {
                      newValues = isSelected ? [] : [optionValue];
                    } else {
                      if (isSelected) {
                        newValues = currentValues.filter(v => v !== optionValue && v !== option && v !== "none" && v !== "None");
                      } else {
                        newValues = [...currentValues.filter(v => v !== "none" && v !== "None"), optionValue];
                      }
                    }
                    onChange(newValues);
                  }}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-300 hover:border-primary-300 hover:bg-primary-50'
                  }`}
                >
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded border-2 mr-3 ${
                      isSelected ? 'bg-primary-500 border-primary-500' : 'border-gray-400'
                    }`}>
                      {isSelected && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="font-medium">{optionLabel}</span>
                  </div>
                </button>
              );
            })}
            <div className="mt-4 pt-4 border-t">
              {(value || []).length > 0 ? (
                <button
                  onClick={() => onChange(value)}
                  className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Continue with {(value || []).length} selection{(value || []).length !== 1 ? 's' : ''}
                </button>
              ) : !question.required ? (
                <button
                  onClick={() => onChange([])}
                  className="w-full py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Skip (None apply)
                </button>
              ) : (
                <p className="text-center text-gray-500 text-sm">
                  Please select at least one option to continue
                </p>
              )}
            </div>
          </div>
        );

      case 'rating_scale':
        return (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              {Array.from({ length: question.max - question.min + 1 }, (_, i) => {
                const rating = question.min + i;
                return (
                  <button
                    key={rating}
                    onClick={() => onChange(rating)}
                    className={`w-12 h-12 rounded-full border-2 transition-all duration-200 ${
                      value === rating
                        ? 'border-primary-500 bg-primary-500 text-white'
                        : 'border-gray-300 hover:border-primary-300 hover:bg-primary-50'
                    }`}
                  >
                    {rating}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{question.labels[question.min]}</span>
              <span>{question.labels[question.max]}</span>
            </div>
            {value && question.labels[value] && (
              <div className="text-center mt-4">
                <span className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                  {question.labels[value]}
                </span>
              </div>
            )}
          </div>
        );

      case 'dropdown':
        // Show Continue button only if value hasn't been set in responses yet (user is on default)
        const showContinueButton = !value && dropdownLocalValue && dropdownLocalValue === question.default;
        return (
          <div className="space-y-4">
            <select
              value={dropdownLocalValue}
              onChange={(e) => {
                const newValue = e.target.value;
                // Only auto-advance if user actually changed the value (different from current displayed value)
                const hasChanged = newValue !== dropdownLocalValue;
                setDropdownLocalValue(newValue);
                // Auto-advance when user changes dropdown value
                if (newValue && hasChanged) {
                  onChange(newValue);
                }
              }}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
            >
              {!dropdownLocalValue && (
                <option value="">Select an option...</option>
              )}
              {question.options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {/* Show Continue button only if default value is shown and user hasn't changed it yet */}
            {showContinueButton && (
              <button
                onClick={() => onChange(dropdownLocalValue)}
                className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Continue
              </button>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center text-gray-500 py-8">
            Unsupported question type: {question.type}
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {renderInput()}
    </div>
  );
};

export default HRAQuestionnaireStreamlined;
